import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { SIGNUP_PATH, SITE_URL } from './seoPages';
import { SeoHead, FaqSection, RelatedLinks, SignupCta } from './SeoShared';
import '../products/EffyOrder.css';
import './SeoPages.css';

// Common rates offered as one-tap chips; any other rate can be typed in.
const RATE_CHIPS = [3, 5, 18, 40];

const FAQS = [
  { question: 'How do I add GST to an amount?', answer: 'Multiply the amount by the GST rate and divide by 100 to get the GST. Add it to the amount for the total. For example, ₹1,000 at 18% GST is ₹180 GST, so the total is ₹1,180.' },
  { question: 'How do I remove GST from an amount that already includes it?', answer: 'Divide the GST-inclusive amount by (1 + rate/100) to get the amount before tax. For example, ₹1,180 including 18% GST is ₹1,000 before tax and ₹180 GST.' },
  { question: 'When is CGST + SGST charged and when is IGST charged?', answer: 'For a sale within the same state (intra-state), GST is split equally into CGST and SGST. For a sale to another state (inter-state), the full GST is charged as IGST.' },
  { question: 'Can Effy Order calculate GST on my invoices automatically?', answer: 'Yes. Set your GST rate per HSN code once and Effy Order applies the correct CGST, SGST or IGST on every invoice.' },
];

const CALCULATOR_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'WebApplication',
  name: 'Free GST Calculator',
  url: `${SITE_URL}/tools/gst-calculator`,
  applicationCategory: 'FinanceApplication',
  operatingSystem: 'Web',
  offers: { '@type': 'Offer', price: '0', priceCurrency: 'INR' },
};

const roundToPaise = (value) => Math.round(value * 100) / 100;

const formatInr = (value) =>
  `₹${value.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// mode 'add'    → amount is before tax; GST is added on top.
// mode 'remove' → amount already includes GST; GST is taken out.
function calculateGst(amount, rate, mode) {
  let netAmount = amount;
  let gstAmount = roundToPaise((amount * rate) / 100);
  if (mode === 'remove') {
    netAmount = roundToPaise(amount / (1 + rate / 100));
    gstAmount = roundToPaise(amount - netAmount);
  }
  const halfGst = roundToPaise(gstAmount / 2);
  return {
    netAmount,
    gstAmount,
    cgst: halfGst,
    sgst: roundToPaise(gstAmount - halfGst),
    totalAmount: roundToPaise(netAmount + gstAmount),
  };
}

export default function GstCalculatorPage() {
  const [amount, setAmount] = useState('1000');
  const [rate, setRate] = useState('18');
  const [mode, setMode] = useState('add');
  const [supply, setSupply] = useState('intra');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const amountNumber = Number(amount) || 0;
  const rateNumber = Number(rate) || 0;
  const result = calculateGst(amountNumber, rateNumber, mode);
  const isIntraState = supply === 'intra';

  return (
    <div className="oi-page-v2">
      <SeoHead
        path="/tools/gst-calculator"
        title="GST Calculator: Add or Remove GST Online | EffyVenture"
        description="Free online GST calculator for India. Add GST to a price or remove GST from an inclusive amount, with CGST + SGST or IGST breakup. Works for any GST rate."
        breadcrumbName="GST Calculator"
        faqs={FAQS}
        extraSchema={CALCULATOR_SCHEMA}
      />

      <section className="oi-hero-v2 seo-hero--center">
        <div className="seo-hero__center">
          <div className="oi-badge-v2">Free tool</div>
          <h1 className="oi-hero-v2__title">
            GST Calculator <span className="text-gradient">— add or remove GST instantly</span>
          </h1>
          <p className="oi-hero-v2__subtitle">
            Enter an amount and a GST rate to see the tax, the CGST + SGST or IGST breakup, and the final amount.
          </p>
        </div>
      </section>

      <section className="seo-calc">
        <div className="seo-calc__card">
          <div className="seo-calc__form">
            <label className="seo-calc__field">
              <span className="seo-calc__label">Amount (₹)</span>
              <input
                className="seo-calc__input"
                type="number" inputMode="decimal" min="0" step="0.01"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
              />
            </label>

            <div className="seo-calc__field">
              <span className="seo-calc__label">GST rate (%)</span>
              <div className="seo-calc__chips">
                {RATE_CHIPS.map((chip) => (
                  <button
                    key={chip}
                    type="button"
                    className={`seo-calc__chip${String(chip) === rate ? ' seo-calc__chip--active' : ''}`}
                    onClick={() => setRate(String(chip))}
                  >
                    {chip}%
                  </button>
                ))}
                <input
                  className="seo-calc__input seo-calc__input--rate"
                  type="number" inputMode="decimal" min="0" max="100" step="0.01"
                  value={rate}
                  onChange={(e) => setRate(e.target.value)}
                  aria-label="Custom GST rate"
                />
              </div>
            </div>

            <div className="seo-calc__field">
              <span className="seo-calc__label">Amount is</span>
              <div className="seo-calc__toggle">
                <button type="button" className={`seo-calc__chip${mode === 'add' ? ' seo-calc__chip--active' : ''}`} onClick={() => setMode('add')}>
                  Before GST (add GST)
                </button>
                <button type="button" className={`seo-calc__chip${mode === 'remove' ? ' seo-calc__chip--active' : ''}`} onClick={() => setMode('remove')}>
                  Including GST (remove GST)
                </button>
              </div>
            </div>

            <div className="seo-calc__field">
              <span className="seo-calc__label">Type of sale</span>
              <div className="seo-calc__toggle">
                <button type="button" className={`seo-calc__chip${isIntraState ? ' seo-calc__chip--active' : ''}`} onClick={() => setSupply('intra')}>
                  Same state (CGST + SGST)
                </button>
                <button type="button" className={`seo-calc__chip${!isIntraState ? ' seo-calc__chip--active' : ''}`} onClick={() => setSupply('inter')}>
                  Other state (IGST)
                </button>
              </div>
            </div>
          </div>

          <dl className="seo-calc__result" aria-live="polite">
            <div className="seo-calc__row">
              <dt>Amount before GST</dt>
              <dd>{formatInr(result.netAmount)}</dd>
            </div>
            {isIntraState && (
              <>
                <div className="seo-calc__row">
                  <dt>CGST ({roundToPaise(rateNumber / 2)}%)</dt>
                  <dd>{formatInr(result.cgst)}</dd>
                </div>
                <div className="seo-calc__row">
                  <dt>SGST ({roundToPaise(rateNumber / 2)}%)</dt>
                  <dd>{formatInr(result.sgst)}</dd>
                </div>
              </>
            )}
            {!isIntraState && (
              <div className="seo-calc__row">
                <dt>IGST ({rateNumber}%)</dt>
                <dd>{formatInr(result.gstAmount)}</dd>
              </div>
            )}
            <div className="seo-calc__row">
              <dt>Total GST</dt>
              <dd>{formatInr(result.gstAmount)}</dd>
            </div>
            <div className="seo-calc__row seo-calc__row--total">
              <dt>Total amount</dt>
              <dd>{formatInr(result.totalAmount)}</dd>
            </div>
          </dl>
        </div>

        <p className="seo-calc__note">
          Billing every day? <Link to={SIGNUP_PATH}>Effy Order</Link> applies the right GST to every invoice automatically, based on HSN code and customer state.
        </p>
      </section>

      <FaqSection faqs={FAQS} />
      <RelatedLinks paths={['/products/effy-order/gst-billing-software', '/products/effy-order/inventory-management-software', '/products/effy-order/quotation-software']} />
      <SignupCta />
    </div>
  );
}
